import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  title: "",
  short_title: "",
  description: "",
  event_date: "",
  start_time: "",
  end_time: "",
  category: "percuma",
  location_type: "online",
  location: "",
  speaker_name: "",
  event_status: "upcoming",
  visibility: "draft",
  registration_link: "",
};

export function EventFormModal({ event, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (event) {
      setForm({ ...EMPTY, ...event });
    } else {
      setForm(EMPTY);
    }
  }, [event, open]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (event?.id) {
        await api.patch(`/admin/events/${event.id}`, form);
        toast.success("Event dikemas kini");
      } else {
        await api.post("/admin/events", form);
        toast.success("Event berjaya dicipta");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error("Failed to save event");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-form-modal">
        <DialogHeader>
          <DialogTitle>{event?.id ? "Edit Event" : "Add Event"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="title">Tajuk Event</Label>
              <Input id="title" value={form.title} onChange={(e) => setField("title", e.target.value)} required data-testid="event-form-title" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="short_title">Tajuk Ringkas</Label>
              <Input id="short_title" value={form.short_title} onChange={(e) => setField("short_title", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="speaker">Speaker / Instructor</Label>
              <Input id="speaker" value={form.speaker_name} onChange={(e) => setField("speaker_name", e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="event_date">Tarikh</Label>
              <Input id="event_date" type="date" value={form.event_date} onChange={(e) => setField("event_date", e.target.value)} required data-testid="event-form-date" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="start_time">Masa Mula</Label>
                <Input id="start_time" type="time" value={form.start_time} onChange={(e) => setField("start_time", e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_time">Masa Expired</Label>
                <Input id="end_time" type="time" value={form.end_time} onChange={(e) => setField("end_time", e.target.value)} required />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setField("category", v)}>
                <SelectTrigger data-testid="event-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percuma">Free</SelectItem>
                  <SelectItem value="berbayar">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Jenis Lokasi</Label>
              <Select value={form.location_type} onValueChange={(v) => setField("location_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="physical">Physical</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="location">Lokasi</Label>
              <Input id="location" value={form.location} onChange={(e) => setField("location", e.target.value)} placeholder="Cth: Zoom, KL Convention Center" />
            </div>

            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="registration_link">Registration Link</Label>
              <Input id="registration_link" type="url" value={form.registration_link} onChange={(e) => setField("registration_link", e.target.value)} placeholder="https://..." />
            </div>

            <div className="space-y-2">
              <Label>Status Event</Label>
              <Select value={form.event_status} onValueChange={(v) => setField("event_status", v)}>
                <SelectTrigger data-testid="event-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="open_registration">Open Registration</SelectItem>
                  <SelectItem value="full">Full</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Visibility</Label>
              <Select value={form.visibility} onValueChange={(v) => setField("visibility", v)}>
                <SelectTrigger data-testid="event-form-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="hidden">Hidden</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" rows={3} value={form.description} onChange={(e) => setField("description", e.target.value)} />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="event-form-save">
              {saving ? "Saving..." : event?.id ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
